import { Box, Button, Drawer, Portal, Spinner, VStack } from "@chakra-ui/react"
import IconButton from "@pathwright/ux/src/components/button/IconButton"
import { useRef } from "react"
import { useHistory } from "react-router-dom"
import { useClickAway, useKey, usePreviousDistinct } from "react-use"
import { PathItemFragment } from "@pathwright/graphql-client/src/generated"
import { usePathNavigator } from "./PathNavigatorContext"
import Highlight from "./PathNavigatorHighlight"
import PathNavigatorItem from "./PathNavigatorItem"
import PathNavigatorSearch from "./PathNavigatorSearch"
import { getIsPathItem } from "./search"
import { NavigatorItem } from "./types"

type PathNavigatorIndexProps = {
  inlineGroupId?: number
}

export const PathNavigatorIndex = ({
  inlineGroupId
}: PathNavigatorIndexProps) => {
  const {
    cohort,
    resource,
    focusedItemId,
    setFocusedItemId,
    setHighlightStyle,
    items,
    setSearch,
    hasHover,
    searchInputRef,
    handleNavigateToItem,
    search,
    searchIsLoading,
    resultsAreLoading
  } = usePathNavigator()

  const history = useHistory()
  const isLoadingSearchResults = !!search && resultsAreLoading && !items?.length

  useKey(
    "ArrowDown",
    (event) => {
      // Allow some other component to prevent this action (i.e. FilterMenuItem.)
      if (event.defaultPrevented) return

      event.preventDefault()

      if (!items.length) return

      const navigableItems = items.filter((i: NavigatorItem) =>
        getIsPathItem(i)
      )

      setFocusedItemId((prevId?: number | string) => {
        if (!prevId) {
          return navigableItems[0].id
        }

        // Find the index of the current item
        const prevIndex = navigableItems.findIndex(
          (i: NavigatorItem) => i.id === prevId
        )

        let nextIndex = prevIndex

        // If not found or already the last item, return unchanged
        if (prevIndex !== -1 && prevIndex !== navigableItems.length - 1) {
          nextIndex = prevIndex + 1
        }

        // Move to the next item
        return navigableItems[nextIndex]?.id
      })
    },
    {},
    [items]
  )

  useKey(
    "ArrowUp",
    (event) => {
      // Allow some other component to prevent this action (i.e. FilterMenuItem.)
      if (event.defaultPrevented) return

      event.preventDefault()

      if (!items.length) return

      const navigableItems = items.filter((i: NavigatorItem) =>
        getIsPathItem(i)
      )

      setFocusedItemId((prevId?: number | string) => {
        if (!prevId) {
          return navigableItems[navigableItems.length - 1].id
        }

        // Find the index of the current item
        const prevIndex = navigableItems.findIndex(
          (i: NavigatorItem) => i.id === prevId
        )

        let nextIndex = prevIndex

        // If not found or already the first item, return unchanged
        if (prevIndex !== -1 && prevIndex !== 0) {
          nextIndex = prevIndex - 1
        }

        // Move to the previous item
        return navigableItems[nextIndex]?.id
      })
    },
    {},
    [items]
  )

  useKey(
    "Enter",
    (e) => {
      // Allow some other component to prevent this action (i.e. FilterMenuItem.)
      if (e.defaultPrevented) return

      // if only one item exists, navigate to it
      if (items.length === 1) {
        handleNavigateToItem(items[0] as PathItemFragment)
        return
      }

      if (focusedItemId === undefined) return

      // Look for item that's currently focused (via up/down arrows)
      const item = items.find((i: NavigatorItem) => {
        return getIsPathItem(i) && i.id === focusedItemId
      })

      if (!item) return

      handleNavigateToItem(item as PathItemFragment)
    },
    {},
    [items, focusedItemId, cohort, resource, history]
  )

  !search && items.length

  // We'll always render the search results status for screen readers.
  // We'll conditionally hide it though if the status is not yet "in sync"
  // with the final search state.
  const prevSearch = usePreviousDistinct(search)
  const isDoneSearching = !!search && !searchIsLoading
  const hasSearchResults = !!prevSearch && !!search && !!items.length
  const hasNoResults = !items.length
  // The original intent of the search results status was for screen readers only. Optimized for the seeing as well,
  // but we'll continue to visually hide it for now (hence the `false &&`).
  const shouldRevealSearchResultsStatus =
    false && (isDoneSearching || hasSearchResults || hasNoResults)

  return (
    <VStack
      gap={1}
      align="start"
      position="relative"
      onMouseLeave={() =>
        setHighlightStyle({ transform: "translateY(0px)", opacity: 0 })
      }
      data-path-navigator-items="true"
    >
      {/* Motion div for a smooth hover highlight */}
      {hasHover ? <Highlight /> : null}
      {/* For screen reader */}
      <div
        id="navigator-search-results-status"
        aria-atomic="true"
        role="status"
        aria-live="polite"
        className={shouldRevealSearchResultsStatus ? "" : "sr-only"}
      >
        {isLoadingSearchResults
          ? "Searching for matching items..."
          : `${items.length || "No"} Path Items found`}
      </div>
      {items?.length ? (
        items
          .filter((i: NavigatorItem) => {
            if (!i) return false
            // Default: include all items
            if (!getIsPathItem(i) || !inlineGroupId) return true

            // For Inline Navigator: only include items that are children of the current group
            if (i.type === "lesson") return i.source_id === inlineGroupId
            if (i.type === "step" || i.type === "resource")
              return i.parent_source_id === inlineGroupId

            return true
          })
          .map((item: NavigatorItem, index: number) => (
            <PathNavigatorItem
              key={`item-${index}-${item.id}`}
              item={item}
              isFocused={focusedItemId === item.id}
            />
          ))
      ) : (
        <VStack justify="start" align="center" h="100%" w="100%">
          {isLoadingSearchResults ? (
            <VStack pt={14} gap={3}>
              <Spinner size="md" color="whiteAlpha.900" />
              <Box fontWeight="700" color="whiteAlpha.900">
                Searching...
              </Box>
            </VStack>
          ) : (
            <>
              <Box pt={14} fontWeight="700" color="whiteAlpha.900">
                0 results
              </Box>
              <Box>
                <Button
                  color="whiteAlpha.900"
                  p={0}
                  variant="plain"
                  textDecoration="underline"
                  onClick={() => {
                    setSearch("")
                    setFocusedItemId(undefined)
                    setHighlightStyle({
                      transform: "translateY(0px)",
                      opacity: 0
                    })
                    if (searchInputRef.current) {
                      searchInputRef.current.value = ""
                    }
                  }}
                >
                  Clear search
                </Button>
              </Box>
            </>
          )}
        </VStack>
      )}
    </VStack>
  )
}

const PathNavigator = () => {
  const { isOpen, onClose, isPhone, searchInputRef, inlineMode, isCollection } =
    usePathNavigator()

  // Since we're not rendering a DrawerOverlay, we need to handle outside clicks manually.
  const drawerRef = useRef<HTMLDivElement>(null)
  useClickAway(drawerRef, () => onClose())

  // Currently not supporting the Path Navigator in Collections
  if (isCollection) return null

  return (
    <Drawer.Root
      open={isOpen && !inlineMode}
      onOpenChange={(e: { open: boolean }) => !e.open && onClose?.()}
      size="sm"
      initialFocusEl={() => (isPhone ? null : searchInputRef.current)}
      preventScroll={false}
    >
      <Portal>
        <Drawer.Positioner p={2} pointerEvents="none">
          <Drawer.Content
            ref={drawerRef}
            borderRadius="card"
            bgColor="blackAlpha.800"
            backdropFilter="blur(20px)"
            borderWidth="0px"
            color="white"
          >
            <Drawer.CloseTrigger asChild>
              <IconButton
                aria-label="close"
                icon="x"
                size="xs"
                variant="ghost"
                colorPalette="whiteAlpha"
              />
            </Drawer.CloseTrigger>
            <Drawer.Header pb={0}>
              <PathNavigatorSearch />
            </Drawer.Header>
            <Drawer.Body
              // Add a "fade out" gradient at the bottom of the drawer
              _after={{
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "70px",
                width: "100%",
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0), var(--chakra-colors-black-alpha-800))",
                borderBottomRightRadius: "10px",
                borderBottomLeftRadius: "10px",
                pointerEvents: "none"
              }}
              overflowY="auto"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              alignItems="stretch"
            >
              <PathNavigatorIndex />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

export default PathNavigator
